import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import PageHeader from '../PageHeader';
import Loader from '../Loader';

// Badge text colours are intentionally fixed: they sit on saturated accent
// backgrounds that keep their hue in dark mode.
const STATUS_BADGE_STYLES = {
  approved: { backgroundColor: 'var(--color-success)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8em', fontWeight: 500 },
  rejected: { backgroundColor: 'var(--color-danger-dk)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8em', fontWeight: 500 },
  pending: { backgroundColor: 'var(--color-warning)', color: '#000', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8em', fontWeight: 500 },
  not_submitted: { backgroundColor: 'var(--color-disabled)', color: 'var(--color-text)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8em', fontWeight: 500 },
};

const getStatusBadge = (managerApproval, isSubmitted, statusId) => {
  if (!isSubmitted) {
    return <span style={STATUS_BADGE_STYLES.not_submitted}>Not Submitted</span>;
  }
  if (managerApproval && managerApproval.approved !== null && managerApproval.approved !== undefined) {
    if (managerApproval.approved === true) {
      return <span style={STATUS_BADGE_STYLES.approved}>Approved</span>;
    }
    return <span style={STATUS_BADGE_STYLES.rejected}>Rejected</span>;
  }
  // Legacy apps without ManagerApproval record
  if (statusId === 4) return <span style={STATUS_BADGE_STYLES.approved}>Approved</span>;
  if (statusId === 5) return <span style={STATUS_BADGE_STYLES.rejected}>Rejected</span>;
  return <span style={STATUS_BADGE_STYLES.pending}>Pending</span>;
};

const daysSinceSubmission = (submittedAt) => {
  if (!submittedAt) return '—';
  const diff = Math.floor((Date.now() - new Date(submittedAt).getTime()) / (1000 * 60 * 60 * 24));
  return diff;
};

const initialCase = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const roleName = (applicantType) => {
  if (applicantType === 'committee' || applicantType === 'commitee') return 'Committee Member';
  return initialCase(applicantType);
};

const ManagerApprovalPage = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [managerSubmissions, setManagerSubmissions] = useState([]);
  const [comments, setComments] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [editing, setEditing] = useState({});
  const [history, setHistory] = useState({});
  const [expanded, setExpanded] = useState({});

  const fetchSubmissions = useCallback(() => {
    if (!currentUser?.user_id) return;

    Promise.all([
      axios.get('/api/applications/my_application'),
      axios.get('/api/manager_approval_type_config'),
    ])
      .then(([response, configRes]) => {
        const currentYear = new Date().getFullYear();
        const typeConfigs = configRes.data || [];

        // Show all current-year apps where user is the manager (not the applicant)
        // and the applicant type requires manager approval for that chapter
        const submissions = response.data.filter((x) => {
          const isCurrentYear = x.year === currentYear;
          const isNotUser = x.user_id !== currentUser.user_id;
          if (!isCurrentYear || !isNotUser) return false;

          // Check type config
          const chapterConfig = typeConfigs.find(c => c.chapter_id === x.chapter_id);
          const config = chapterConfig || { mentee_required: true, mentor_required: false, tcm_required: false };
          const appType = (x.ApplicantType?.applicant_type || '').toLowerCase();
          if (appType === 'mentee') return config.mentee_required;
          if (appType === 'mentor') return config.mentor_required;
          if (appType === 'commitee' || appType === 'committee') return config.tcm_required;
          return true;
        });
        setManagerSubmissions(submissions);
      })
      .catch((error) => {
        console.error('Error fetching manager submissions:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentUser]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Socket.io subscription for real-time updates
  useEffect(() => {
    const socket = io({ path: '/socket.io/', transports: ['websocket', 'polling'], withCredentials: true });

    const handleUpdate = ({ application_id }) => {
      // Re-fetch the specific application
      axios.get('/api/applications/my_application').then((response) => {
        const currentYear = new Date().getFullYear();
        const submissions = response.data.filter((x) => {
          const isCurrentYear = x.year === currentYear;
          const isNotUser = x.user_id !== currentUser.user_id;
          return isCurrentYear && isNotUser;
        });
        setManagerSubmissions(submissions);
      });
    };

    const handleConfigUpdate = () => {
      // Re-fetch when type config changes (applications may appear/disappear)
      fetchSubmissions();
    };

    socket.on('manager-approval:updated', handleUpdate);
    socket.on('manager-approval-config:updated', handleConfigUpdate);

    return () => {
      socket.off('manager-approval:updated', handleUpdate);
      socket.off('manager-approval-config:updated', handleConfigUpdate);
      socket.disconnect();
    };
  }, [currentUser, fetchSubmissions]);

  const fetchHistory = useCallback(async (applicationId) => {
    try {
      const res = await axios.get(`/api/applications/${applicationId}/approve/history`);
      setHistory((prev) => ({ ...prev, [applicationId]: res.data }));
    } catch (err) {
      console.error('Error fetching approval history:', err);
    }
  }, []);

  // Fetch history for all submitted apps that have an approval record
  useEffect(() => {
    managerSubmissions.forEach((sub) => {
      if (sub.ManagerApproval && !history[sub.id]) {
        fetchHistory(sub.id);
      }
    });
  }, [managerSubmissions, fetchHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApproval = async (applicationId, approved) => {
    const comment = comments[applicationId]?.trim();
    if (!comment) return;

    setSubmitting((prev) => ({ ...prev, [applicationId]: true }));

    try {
      await axios.post(`/api/applications/${applicationId}/approve`, {
        application_id: applicationId,
        approved,
        comments: comment,
      });
      // Clear editing state on success
      setComments((prev) => ({ ...prev, [applicationId]: '' }));
      setEditing((prev) => ({ ...prev, [applicationId]: false }));
      // Refresh list and history
      fetchSubmissions();
      fetchHistory(applicationId);
    } catch (error) {
      console.error('Error submitting approval:', error);
      alert(error.response?.data?.error || 'Failed to submit approval');
    } finally {
      setSubmitting((prev) => ({ ...prev, [applicationId]: false }));
    }
  };

  const startEditing = (sub) => {
    setEditing((prev) => ({ ...prev, [sub.id]: true }));
    setComments((prev) => ({ ...prev, [sub.id]: sub.ManagerApproval?.comments || '' }));
  };

  const cancelEditing = (applicationId) => {
    setEditing((prev) => ({ ...prev, [applicationId]: false }));
    setComments((prev) => ({ ...prev, [applicationId]: '' }));
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <PageHeader title="Manager Approvals" />
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>
        {managerSubmissions.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'var(--color-text-faded)', marginTop: '40px' }}>
            No manager approvals at this time.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {managerSubmissions.map((sub) => {
              const isSubmitted = sub.status_id >= 2 && sub.status_id !== 3 && !!sub.submitted_at;
              const isPending = isSubmitted && sub.status_id === 2 && (!sub.ManagerApproval || sub.ManagerApproval.approved === null || sub.ManagerApproval.approved === undefined);
              const isEditing = editing[sub.id];
              const showForm = isSubmitted && (isPending || isEditing);
              const commentValue = comments[sub.id] || '';
              const isSubmitting = submitting[sub.id];
              const appHistory = history[sub.id] || [];

              return (
                <div key={sub.id} style={{ border: '1px solid var(--color-border)', borderRadius: '8px', padding: '16px', backgroundColor: 'var(--color-surface)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '1.1em' }}>{sub.User?.name || 'Unknown'}</div>
                      <div style={{ color: 'var(--color-text-faded)', fontSize: '0.9em' }}>
                        Role: {roleName(sub.ApplicantType?.applicant_type || '')}
                      </div>
                      <div style={{ color: 'var(--color-text-faded)', fontSize: '0.85em' }}>
                        {isSubmitted
                          ? `Submitted ${daysSinceSubmission(sub.submitted_at)} day(s) ago`
                          : 'Application in progress'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {getStatusBadge(sub.ManagerApproval, isSubmitted, sub.status_id)}
                      {isSubmitted && !isPending && !isEditing && (
                        <button
                          onClick={() => startEditing(sub)}
                          style={{ padding: '4px 10px', fontSize: '0.82em', borderRadius: '4px', border: '1px solid var(--color-border-input)', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer' }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Show current response read-only when NOT editing */}
                  {isSubmitted && !isPending && !isEditing && sub.ManagerApproval?.comments && (
                    <div style={{ backgroundColor: 'var(--color-surface-alt)', padding: '8px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.9em' }}>
                      <strong>Your comment:</strong> {sub.ManagerApproval.comments}
                    </div>
                  )}

                  {/* Info message for unsubmitted applications */}
                  {!isSubmitted && (
                    <div style={{ backgroundColor: 'var(--color-surface-alt)', padding: '8px 12px', borderRadius: '4px', fontSize: '0.9em', color: 'var(--color-text-faded)', fontStyle: 'italic' }}>
                      This applicant has not yet submitted their application. You will be able to review and approve once they submit.
                    </div>
                  )}

                  {/* Form for pending OR editing */}
                  {showForm && (
                    <div>
                      <textarea
                        placeholder="Enter a comment (required)..."
                        value={commentValue}
                        onChange={(e) => { const val = e.target.value; setComments((prev) => ({ ...prev, [sub.id]: val })); }}
                        style={{ width: '100%', minHeight: '60px', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-border-input)', resize: 'vertical', marginBottom: '8px', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' }}
                        disabled={isSubmitting}
                      />
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => handleApproval(sub.id, true)}
                          disabled={!commentValue.trim() || isSubmitting}
                          style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', backgroundColor: commentValue.trim() ? 'var(--color-success)' : 'var(--color-disabled)', color: '#fff', cursor: commentValue.trim() ? 'pointer' : 'not-allowed', fontWeight: 500 }}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleApproval(sub.id, false)}
                          disabled={!commentValue.trim() || isSubmitting}
                          style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', backgroundColor: commentValue.trim() ? 'var(--color-danger-dk)' : 'var(--color-disabled)', color: '#fff', cursor: commentValue.trim() ? 'pointer' : 'not-allowed', fontWeight: 500 }}
                        >
                          Reject
                        </button>
                        {isEditing && (
                          <button
                            onClick={() => cancelEditing(sub.id)}
                            style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid var(--color-border-input)', backgroundColor: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer', fontWeight: 500 }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Audit trail — visible via chevron toggle, independent of edit mode */}
                  {appHistory.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <button
                        onClick={() => setExpanded((prev) => ({ ...prev, [sub.id]: !prev[sub.id] }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontSize: '0.85em', fontWeight: 600, color: 'var(--color-text-faded)', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <span style={{ display: 'inline-block', transform: expanded[sub.id] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>&#9654;</span>
                        History ({appHistory.length})
                      </button>
                      {expanded[sub.id] && (
                        <div style={{ marginTop: '8px', borderTop: '1px solid var(--color-border)', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {[...appHistory].reverse().map((entry, i) => {
                          let newVal = {};
                          let prevVal = {};
                          try { newVal = JSON.parse(entry.newValue || '{}'); } catch { /* ignore */ }
                          try { prevVal = JSON.parse(entry.previousValue || '{}'); } catch { /* ignore */ }
                          const action = newVal.approved === true ? 'Approved' : newVal.approved === false ? 'Rejected' : 'Updated';
                          const isOverride = newVal.is_override;
                          const who = entry.sourceUser?.name || 'Unknown';
                          const when = new Date(entry.createdTimestamp).toLocaleString();
                          const comment = newVal.comments || '';
                          const prevAction = prevVal.approved === true ? 'Approved' : prevVal.approved === false ? 'Rejected' : null;

                          return (
                            <div key={entry.id || i} style={{ fontSize: '0.84em', padding: '6px 10px', backgroundColor: 'var(--color-surface-alt)', borderRadius: '4px', borderLeft: '3px solid var(--color-primary)' }}>
                              <div>
                                <strong>{who}</strong>
                                {isOverride && <span style={{ color: 'var(--color-primary)', marginLeft: '4px' }}>(override)</span>}
                                {' — '}
                                <span style={{ fontWeight: 500 }}>{action}</span>
                                {prevAction && <span style={{ color: 'var(--color-text-faded)' }}> (was: {prevAction})</span>}
                              </div>
                              {comment && <div style={{ color: 'var(--color-text-faded)', marginTop: '2px' }}>"{comment}"</div>}
                              <div style={{ color: 'var(--color-text-faded)', fontSize: '0.9em', marginTop: '2px' }}>{when}</div>
                            </div>
                          );
                        })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p style={{ textAlign: 'center', color: 'var(--color-text-faded)', marginTop: '24px', fontSize: '0.85em', fontStyle: 'italic' }}>
          <b>Note:</b> Due to space limitations, manager approval does not guarantee
          the applicant will be included in this year's CLEAR program.
        </p>
      </div>
    </div>
  );
};

export default ManagerApprovalPage;
